import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import GoogleMap from "google-map-react";
import utils from "@utils";
import { GOOGLE_MAP_SETTINGS } from "@constants";
import { StateSelection } from "@components/common";
import {
	LocationService,
	GenericService,
	JurisdictionLocationService,
	ZoneService,
} from "@services";
import Loader from "../Loader/Loader";
import ReactSelect from "../ReactSelect/ReactSelect";
import Modal from "../Modal/Modal";
import MapMarker from "../MapMarker/MapMarker";

import "./LocationModal.scss";

class LocationModal extends Component {
	constructor(props) {
		super(props);
		this.initialState = {
			loading: false,
			locationTypesLoading: false,
			mapLoading: false,
			showUseLocationButton: false,
			lastSearchString: "",
			locationTypes: [],
			errors: [],
			savingLocation: false,
			googleMapCenter: GOOGLE_MAP_SETTINGS.defaultCenter,
			suggestedLocation: {},
			name: "",
			streetAddress1: "",
			city: "",
			stateId: "",
			zipcode: "",
			county: "",
			googlePlaceId: "",
			locationTypeId: "",
			clientSupervisorContactId: null,
			approvedStatus: null,
			customCoordinates: false,
			validLocation: {},
			ZoneId: null,
			zones: [],
		};

		this.state = { ...this.initialState };
		this._stateSelection = createRef();
	}

	componentDidMount() {
		const { lang } = global;
		this.setState({ locationTypesLoading: true });
		GenericService.getLocationTypes()
			.then(({ data }) => {
				const locationTypes = data.map(({ Id: value, Name: label }) => ({ value, label }));
				this.initialState.locationTypes = locationTypes;
				this.setState({ locationTypes });
			})
			.catch(() =>
				utils.notify({
					type: "error",
					message: lang.t("Error loading location types"),
					timeOut: 2500,
				}),
			)
			.finally(() => this.setState({ locationTypesLoading: false }));
	}

	componentDidUpdate = lastProps => {
		const { show: prevShow, locationId: prevLocationId } = lastProps;
		const { show, locationId, jobRequestProperties } = this.props;

		/** When the locationId changes and the modal is open and the locationId is available
		 * means that we want to edit the location details, so we start the loading animation
		 * and call getLocationById right away */
		if (show && prevLocationId !== locationId && locationId !== null) {
			this.getLocationById(locationId);
		}

		if (
			prevShow !== show &&
			show &&
			Object.keys(jobRequestProperties).length > 0 &&
			jobRequestProperties.prefillLocation
		) {
			this.prefillLocation();
		}
	};

	getMappedZones = data => {
		if (data.length !== 0) {
			return data.map(item => ({ value: item.Id, label: item.Name }));
		}
		return [];
	};

	getZones = () => {
		ZoneService.getAll()
			.then(({ data }) => {
				this.initialState.zones = this.getMappedZones(data);
				this.setState({ zones: this.getMappedZones(data) });
			})
			.catch(() => {
				const { lang } = global;
				utils.notify({
					type: "error",
					message: lang.t("Error loading Location Zones"),
					title: "Error",
				});
			});
	};

	/** Wrapper method to update a location's address details when they are passed via props */
	prefillLocation = () => {
		const { jobRequestProperties: request } = this.props;
		this.setState(
			{
				name: request.locationName,
				streetAddress1: request.streetAddress,
				city: request.city,
				stateId: request.stateId,
				zipcode: request.zipCode,
				locationTypeId: request.locationTypeId,
			},
			() => {
				if (this._stateSelection.current) this.updateMap();
			},
		);
	};

	/**
	 * Retrieve a specific location's details and fill the modal with appropriate details
	 * @param id The unique ID of the location to be retrieved
	 */
	getLocationById = id => {
		const { lang } = global;
		this.setState({ loading: true });

		LocationService.getById(id)
			.then(({ data }) => {
				this.setState({
					loading: false,
					name: data.Name || "",
					streetAddress1: data.StreetAddress1 || "",
					city: data.City || "",
					stateId: data.StateId || "",
					zipcode: data.Zipcode || "",
					ZoneId: data.ZoneId || "",
					locationTypeId: data.LocationTypeId || "",
					clientSupervisorContactId: data.ClientSupervisorContactId,
					customCoordinates: data.CustomCoordinates,
					googleMapCenter: {
						lat: data.Latitude,
						lng: data.Longitude,
					},
				});
			})
			.catch(({ response: { status } }) => {
				if (status === 404) this.onCancel();
				utils.notify({
					type: "error",
					message:
						status === 404
							? lang.t("Location not found")
							: lang.t("Error loading location details"),
					timeOut: 2000,
				});
			});
	};

	/**
	 * Validates that all form fields are filled out appropriately based on how location details have been entered
	 * @returns {Boolean} whether the current form state is valid or not
	 */
	validateForm = () => {
		const {
			name,
			streetAddress1,
			city,
			stateId,
			zipcode,
			googleMapCenter,
			locationTypeId,
			customCoordinates,
			approvedStatus,
		} = this.state;
		const { fromLocationApprovals } = this.props;

		const errors = [];
		if (name.trim() === "") errors.push("name");
		if (stateId === "") errors.push("stateId");
		if (Number.isNaN(parseFloat(googleMapCenter.lat))) errors.push("lat");
		if (Number.isNaN(parseFloat(googleMapCenter.lng))) errors.push("lng");
		if (locationTypeId === "") errors.push("locationTypeId");

		// When we are not using custom coordinates, validate that all address fields are filled in
		if (!customCoordinates) {
			if (streetAddress1.trim() === "") errors.push("streetAddress1");
			if (city.trim() === "") errors.push("city");
			if (zipcode.trim() === "" || zipcode.trim().length < 5) errors.push("zipcode");
		}

		if (fromLocationApprovals) {
			if (!approvedStatus) errors.push("approvedStatus");
		}

		this.setState({ errors });
		if (errors.length > 0) return false;

		return true;
	};

	/** Creates a location along with the approval status provided by the user */
	saveLocationWithApproval = () => {
		const { lang } = global;
		const {
			name: locationName,
			streetAddress1: streetAddress,
			city,
			county,
			stateId,
			zipcode: zipCode,
			googlePlaceId,
			approvedStatus,
			locationTypeId,
			customCoordinates,
			googleMapCenter: { lat: latitude, lng: longitude },
		} = this.state;
		const { onSuccess } = this.props;

		this.setState({ savingLocation: true });

		const dataParams = {
			locationName,
			streetAddress,
			zipCode,
			latitude,
			longitude,
			locationTypeId,
			googlePlaceId,
			city,
			county,
			stateId,
			customCoordinates,
			approved: approvedStatus === "approved",
		};

		JurisdictionLocationService.create({ dataParams })
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Location created successfully"),
					timeOut: 2500,
				});
				onSuccess();
				this.setState(this.initialState);
			})
			.catch(() => {
				this.setState({ savingLocation: false });
				utils.notify({
					type: "error",
					message: lang.t("Error saving location"),
					timeOut: 1500,
				});
			});
	};

	/**
	 * Validates the location details entered in the form against valid Google Maps addresses prior to saving.
	 * This ensures that database entries are accurate and have all the necessary information.
	 */
	validateLocation = () => {
		const { lang } = global;
		const {
			streetAddress1,
			city,
			stateId,
			zipcode,
			customCoordinates,
			googleMapCenter: { lat, lng },
		} = this.state;

		// If the current form state is not valid, do not proceed with address validation
		if (!this.validateForm()) return;

		const { USStates } = this._stateSelection.current.state;
		const USState = USStates.find(({ value }) => value === stateId);

		// We repeat the process used to suggest an address to the user one more time
		let address = `${streetAddress1}, ${city}, ${USState.label.substr(0, 2)} ${zipcode}, USA`;
		if (customCoordinates) address = `${lat}, ${lng}`;

		LocationService.getGeoLocation(address)
			.then(({ data: { results } }) => {
				if (results.length === 0) {
					utils.notify({
						type: "error",
						message: lang.t("Unable to validate address"),
						timeOut: 2000,
					});
					return;
				}

				const [data] = results;
				const validLocation = {};

				/* eslint-disable camelcase */
				const keyMap = {
					locality: "city",
					administrative_area_level_2: "county",
					administrative_area_level_1: "state",
					street_number: "streetNo",
					route: "street",
					postal_code: "zipcode",
				};
				/* eslint-enable camelcase */

				data.address_components.forEach(obj => {
					Object.keys(keyMap).forEach(key => {
						if (obj.long_name && obj.types.includes(key)) {
							validLocation[keyMap[key]] =
								key === "route" || key === "administrative_area_level_1"
									? obj.short_name
									: obj.long_name;
						}
					});
				});
				validLocation.googlePlaceId = data.place_id;
				if (!customCoordinates) validLocation.googleMapCenter = data.geometry.location;
				validLocation.formatted = data.formatted_address;

				// Set the valid location retrieved from Google Maps to confirm with the user prior to submission
				this.setState({ validLocation });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Unable to validate address"),
					timeOut: 2000,
				});
			});
	};

	/** Handles saving of location after the user has confirmed it against a Google Maps-validated address from `this.validateLocation()` */
	confirmLocationCreation = () => {
		this.setState(
			prevState => {
				const { customCoordinates, validLocation } = prevState;
				const { USStates } = this._stateSelection.current.state;
				const stateId = USStates.find(
					({ label }) => label.substr(0, 2) === validLocation.state,
				).value;

				let streetAddress1 = validLocation.streetNo || "";
				if (streetAddress1) streetAddress1 += " ";
				streetAddress1 += validLocation.street || "";

				return {
					streetAddress1,
					city: validLocation.city,
					county: validLocation.county,
					stateId,
					zipcode: validLocation.zipcode,
					googlePlaceId: validLocation.googlePlaceId,
					googleMapCenter: customCoordinates
						? prevState.googleMapCenter
						: validLocation.googleMapCenter,
				};
			},
			() => {
				const { fromLocationApprovals } = this.props;
				if (fromLocationApprovals) this.saveLocationWithApproval();
				else this.saveLocation();
			},
		);
	};

	/** Creates a new location or updates an existing location */
	saveLocation = () => {
		const { lang } = global;
		const {
			name,
			streetAddress1,
			city,
			county,
			stateId,
			zipcode,
			googlePlaceId,
			clientSupervisorContactId,
			locationTypeId,
			customCoordinates,
			googleMapCenter,
			ZoneId,
		} = this.state;

		const { locationId, projectId, onSuccess } = this.props;

		this.setState({ savingLocation: true });

		const data = {
			projectId,
			name,
			streetAddress1,
			city,
			county,
			stateId,
			zipcode,
			locationTypeId,
			googlePlaceId,
			clientSupervisorContactId,
			customCoordinates,
			locationId: locationId || undefined,
			latitude: googleMapCenter.lat,
			longitude: googleMapCenter.lng,
			ZoneId,
		};

		LocationService.save(data)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Location created successfully"),
					timeOut: 1500,
				});
				onSuccess();
				this.setState(this.initialState);
			})
			.catch(() => {
				this.setState({ savingLocation: false });
				utils.notify({
					type: "error",
					message: lang.t("Error creating location"),
					timeOut: 1500,
				});
			});
	};

	/** Calls the prop method `onShow` to reload the current available zones */
	onOpen = () => {
		this.getZones();
	};

	/** Calls the prop method `onHide` to close the modal and resets local state */
	onCancel = () => {
		const { onHide } = this.props;
		onHide();
		this.setState(this.initialState);
	};

	/**
	 * Handles all form input changes except coordinates
	 * @param {String} property The property to update value for
	 */
	handleChange = property => event => {
		let value;

		if (
			property === "locationTypeId" ||
			property === "clientSupervisorContactId" ||
			property === "ZoneId"
		) {
			// indicates usage of react-select
			value = event?.value || "";
		} else {
			({
				target: { value },
			} = event);
		}

		if (property === "zipcode") {
			value = value.replace(/[^0-9]/gi, "");
		} else if (property === "stateId") {
			value = Number.isNaN(parseInt(value, 10)) ? "" : parseInt(value, 10);
		}

		this.setState({
			[property]: value,
		});
	};

	/**
	 * Handles changes to latitude and longitude inputs
	 * @param {String} property The property to update value for (should be either `lat` or `lng`)
	 */
	editMapCoordinates = property => event => {
		const {
			target: { value },
		} = event;

		this.setState(prevState => ({
			customCoordinates: true,
			googleMapCenter: {
				...prevState.googleMapCenter,
				[property]: value.replace(/[^0-9-.]/gi, ""),
			},
		}));
	};

	/** Updates the map marker to best reflect the location specified by the current form inputs */
	updateMap = () => {
		const { lang } = global;
		const {
			lastSearchString,
			streetAddress1,
			city,
			stateId,
			zipcode,
			googleMapCenter: { lat, lng },
			customCoordinates,
		} = this.state;
		const { fromLocationApprovals, jobRequestProperties } = this.props;
		const { USStates } = this._stateSelection.current.state;
		const USState = USStates.find(({ value }) => value === stateId);

		// Construct the search string using only available values
		let searchString = "";
		if (customCoordinates) searchString = `${lat}, ${lng}`;
		else {
			if (streetAddress1) searchString += `${streetAddress1}, `;
			if (city) searchString += `${city}, `;
			if (USState?.label) searchString += `${USState.label.substr(0, 2)}, `;
			if (zipcode) searchString += `${zipcode}, `;
			// If searchString is not empty, append USA to the end of the address
			if (searchString) searchString += "USA";
		}

		// Only request from API if query has changed
		// RKP-1778: Also request if we are prefilling location for job requests
		if (
			(searchString && searchString !== lastSearchString) ||
			jobRequestProperties.prefillLocation
		) {
			this.setState({ mapLoading: true });

			LocationService.getGeoLocation(searchString)
				.then(({ data: { results } }) => {
					if (results.length === 0) {
						this.setState({ mapLoading: false, lastSearchString: searchString });
						return;
					}

					const [data] = results;
					const suggestedLocation = {};
					/* eslint-disable camelcase */
					const labelMap = {
						locality: "city",
						administrative_area_level_2: "county",
						administrative_area_level_1: "state",
						street_number: "streetNo",
						route: "street",
						postal_code: "zipcode",
					};
					/* eslint-enable camelcase */

					// normalize the address properties based on the types reported
					data.address_components.forEach(obj => {
						Object.keys(labelMap).forEach(key => {
							if (obj.long_name && obj.types.includes(key)) {
								suggestedLocation[labelMap[key]] =
									key === "route" ? obj.short_name : obj.long_name;
							}
						});
					});

					suggestedLocation.placeId = data.place_id;
					suggestedLocation.formatted = data.formatted_address;

					/** The suggested googlePlaceId will be used if the user don't select the suggested location. */
					this.setState(prevState => ({
						showUseLocationButton: true,
						googleMapCenter: customCoordinates
							? prevState.googleMapCenter
							: data.geometry.location,
						mapLoading: false,
						googlePlaceId: fromLocationApprovals
							? data.place_id
							: prevState.googlePlaceId,
						lastSearchString: searchString,
						suggestedLocation,
					}));
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error loading google map location"),
						timeOut: 2000,
					});
					this.setState({ mapLoading: false });
				});
		}
	};

	/** Autofills all form inputs with values from the suggested address retrieved by Google Maps */
	useSuggestion = () => {
		const { suggestedLocation } = this.state;
		const { USStates } = this._stateSelection.current.state;

		const { streetNo, street, city, state, zipcode, county, placeId } = suggestedLocation;
		this.setState(prevState => ({
			customCoordinates: false,
			streetAddress1: `${streetNo || ""} ${street || ""}`.trim(),
			city: city || "",
			stateId: state
				? USStates.find(({ label }) => label.substr(5) === state).value
				: prevState.stateId,
			zipcode: zipcode || "",
			county: county || "",
			googlePlaceId: placeId || "",
			showUseLocationButton: false,
		}));
	};

	render() {
		const { lang } = global;
		const {
			loading,
			locationTypesLoading,
			mapLoading,
			showUseLocationButton,
			locationTypes,
			errors,
			suggestedLocation,
			name,
			streetAddress1,
			city,
			stateId,
			zipcode,
			locationTypeId,
			clientSupervisorContactId,
			approvedStatus,
			googleMapCenter,
			savingLocation,
			validLocation,
			customCoordinates,
			ZoneId,
			zones,
		} = this.state;
		const { show, title, contacts, fromLocationApprovals, user, zonesLoading } = this.props;

		const lat = Number.isNaN(parseFloat(googleMapCenter.lat))
			? 0
			: parseFloat(googleMapCenter.lat);
		const lng = Number.isNaN(parseFloat(googleMapCenter.lng))
			? 0
			: parseFloat(googleMapCenter.lng);

		return (
			<Modal
				id="location-modal"
				data-test-id="location-modal"
				show={show}
				onHide={this.onCancel}
				onShow={this.onOpen}
				size={Object.keys(validLocation).length > 0 ? undefined : "lg"}
			>
				<Modal.Header closeButton>
					<Modal.Title className="text-center">{title}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row">
						<div
							className={
								Object.keys(validLocation).length > 0
									? "col-md-12"
									: "col-md-5 col-md-push-7"
							}
						>
							<Choose>
								<When condition={Object.keys(validLocation).length === 0}>
									<div className="suggested-address text-center">
										{suggestedLocation.formatted}
										<If condition={showUseLocationButton}>
											<br />
											<button
												type="button"
												className="btn btn-link btn-sm"
												onClick={this.useSuggestion}
												data-test-id="location-modal__use-suggestion"
											>
												{lang.t("Use this location")}
											</button>
										</If>
									</div>
								</When>
								<Otherwise>
									<Choose>
										<When
											condition={
												(!validLocation.streetNo ||
													!validLocation.street) &&
												!customCoordinates
											}
										>
											{lang.t(
												"A street address could not be found for the entered location_ Do you want to save this location anyway_",
											)}
										</When>
										<Otherwise>
											{lang.t("Please confirm this is the correct address_")}
										</Otherwise>
									</Choose>
									<div className="confirm-address text-center">
										{validLocation.streetNo} {validLocation.street}
										<br />
										{validLocation.formatted.replace(
											`${validLocation.streetNo} ${validLocation.street}, `,
											"",
										)}
										<If condition={customCoordinates}>
											<br />
											{googleMapCenter.lat}, {googleMapCenter.lng}
										</If>
									</div>
								</Otherwise>
							</Choose>
							<div className="location-modal-map">
								<GoogleMap
									zoom={15}
									center={{ lat, lng }}
									bootstrapURLKeys={{ key: GOOGLE_MAP_SETTINGS.key }}
									options={GOOGLE_MAP_SETTINGS}
								>
									<MapMarker
										lat={lat}
										lng={lng}
									/>
								</GoogleMap>
							</div>
							<Loader active={mapLoading} />
						</div>
						<div
							className={
								Object.keys(validLocation).length > 0
									? "hidden"
									: "col-md-7 col-md-pull-5"
							}
						>
							<form className="form-horizontal">
								<div className="form-group">
									<div className="col-sm-4 label-container">
										<label
											htmlFor="locationName"
											className={
												errors.includes("name") ? "text-danger" : undefined
											}
										>
											{lang.t("Location Name")}
										</label>
									</div>
									<div className="col-sm-8">
										<input
											name="locationName"
											id="locationName"
											data-test-id="location-modal__name"
											className={`form-control ${errors.includes("name") ? "invalid-input" : ""}`}
											value={name}
											onChange={this.handleChange("name")}
										/>
									</div>
								</div>
								<div className="form-group">
									<div className="col-sm-4 label-container">
										<label
											htmlFor="streetAddress1"
											className={
												errors.includes("streetAddress1")
													? "text-danger"
													: undefined
											}
										>
											{lang.t("Address")}
										</label>
									</div>
									<div className="col-sm-8">
										<input
											name="streetAddress1"
											id="streetAddress1"
											data-test-id="location-modal__address"
											className={`form-control ${errors.includes("streetAddress1") ? "invalid-input" : ""}`}
											value={streetAddress1}
											onChange={this.handleChange("streetAddress1")}
											onBlur={this.updateMap}
										/>
									</div>
								</div>
								<div className="form-group">
									<div className="col-sm-4 label-container">
										<label
											htmlFor="locationCity"
											className={
												errors.includes("city") ? "text-danger" : undefined
											}
										>
											{lang.t("City")}
										</label>
									</div>
									<div className="col-sm-8">
										<input
											name="locationCity"
											id="locationCity"
											data-test-id="location-modal__city"
											className={`form-control ${errors.includes("city") ? "invalid-input" : ""}`}
											value={city}
											onChange={this.handleChange("city")}
											onBlur={this.updateMap}
										/>
									</div>
								</div>
								<div className="form-group">
									<div className="col-sm-4 label-container">
										<label
											htmlFor="locationStateId"
											className={
												errors.includes("stateId")
													? "text-danger"
													: undefined
											}
										>
											{lang.t("State")}
										</label>
									</div>
									<div className="col-sm-8">
										<StateSelection
											ref={this._stateSelection}
											name="locationStateId"
											id="locationStateId"
											dataTestId="location-modal__state"
											className={`form-control ${errors.includes("stateId") ? "invalid-input" : ""}`}
											value={stateId}
											onChange={this.handleChange("stateId")}
											onBlur={
												customCoordinates
													? undefined
													: () => this.updateMap()
											}
										/>
									</div>
								</div>
								<div className="form-group">
									<div className="col-sm-4 label-container">
										<label
											htmlFor="locationZip"
											className={
												errors.includes("zipcode")
													? "text-danger"
													: undefined
											}
										>
											{lang.t("Zip Code")}
										</label>
									</div>
									<div className="col-sm-8">
										<input
											name="locationZip"
											id="locationZip"
											data-test-id="location-modal__zipcode"
											maxLength="5"
											className={`form-control ${errors.includes("zipcode") ? "invalid-input" : ""}`}
											value={zipcode}
											onChange={this.handleChange("zipcode")}
											onBlur={this.updateMap}
										/>
									</div>
								</div>
								<If condition={user.Organization.UseZones}>
									<div className="form-group">
										<div className="col-sm-4 label-container">
											<label
												htmlFor="ZoneId"
												className={
													errors.includes("ZoneId")
														? "text-danger"
														: undefined
												}
											>
												{lang.t("Zone")}
											</label>
										</div>
										<div className="col-sm-8">
											<ReactSelect
												className={
													errors.includes("ZoneId")
														? "invalid-input"
														: undefined
												}
												name="ZoneId"
												id="ZoneId"
												value={ZoneId}
												onChange={this.handleChange("ZoneId")}
												options={zones}
												isLoading={zonesLoading}
												isClearable
												dataTestId="location-modal__zone"
											/>
										</div>
									</div>
								</If>
								<div className="form-group">
									<div className="col-sm-4 label-container">
										<label
											htmlFor="locationTypeId"
											className={
												errors.includes("locationTypeId")
													? "text-danger"
													: undefined
											}
										>
											{lang.t("Location Type")}
										</label>
									</div>
									<div className="col-sm-8">
										<ReactSelect
											className={
												errors.includes("locationTypeId")
													? "invalid-input"
													: undefined
											}
											name="locationTypeId"
											id="locationTypeId"
											value={locationTypeId}
											onChange={this.handleChange("locationTypeId")}
											options={locationTypes}
											isLoading={locationTypesLoading}
											isClearable
											dataTestId="location-modal__type"
										/>
									</div>
								</div>
								<div className="form-group">
									<div className="col-sm-4 label-container">
										<label
											htmlFor="latitude"
											className={
												errors.includes("lat") ? "text-danger" : undefined
											}
										>
											{lang.t("Latitude")}
										</label>
									</div>
									<div className="col-sm-8">
										<input
											name="latitude"
											id="latitude"
											data-test-id="location-modal__latitude"
											className={`form-control ${errors.includes("lat") ? "invalid-input" : ""}`}
											value={googleMapCenter.lat}
											onChange={this.editMapCoordinates("lat")}
											onBlur={this.updateMap}
										/>
									</div>
								</div>
								<div className="form-group">
									<div className="col-sm-4 label-container">
										<label
											htmlFor="longitude"
											className={
												errors.includes("lng") ? "text-danger" : undefined
											}
										>
											{lang.t("Longitude")}
										</label>
									</div>
									<div className="col-sm-8">
										<input
											name="longitude"
											id="longitude"
											data-test-id="location-modal__longitude"
											className={`form-control ${errors.includes("lng") ? "invalid-input" : ""}`}
											value={googleMapCenter.lng}
											onChange={this.editMapCoordinates("lng")}
											onBlur={this.updateMap}
										/>
									</div>
								</div>
								<If condition={contacts.length > 0}>
									<div className="form-group">
										<div className="col-sm-4 label-container">
											<label htmlFor="clientSupervisor">
												{lang.t("Contact")}
											</label>
										</div>
										<div className="col-sm-8">
											<ReactSelect
												name="clientSupervisor"
												id="clientSupervisor"
												dataTestId="location-modal__client-supervisor"
												placeholder={lang.t("Select Contact")}
												value={clientSupervisorContactId}
												onChange={this.handleChange(
													"clientSupervisorContactId",
												)}
												options={contacts}
												isClearable
											/>
										</div>
									</div>
								</If>
								<If condition={fromLocationApprovals}>
									<div className="form-group">
										<div
											className={`col-sm-4 radio-label label-container ${errors.includes("approvedStatus") ? "text-danger" : ""}`}
										>
											{lang.t("Initial Status")}
										</div>
										<div className="col-sm-8">
											<div className="radio">
												<label htmlFor="approvedStatus_approve">
													<input
														type="radio"
														name="approvedStatus"
														value="approved"
														id="approvedStatus_approve"
														data-test-id="location-modal__approved-status-approve"
														onChange={this.handleChange(
															"approvedStatus",
														)}
														checked={approvedStatus === "approved"}
													/>
													{lang.t("Approved")}
												</label>
											</div>
											<div className="radio">
												<label htmlFor="approvedStatus_deny">
													<input
														type="radio"
														name="approvedStatus"
														value="denied"
														id="approvedStatus_deny"
														data-test-id="location-modal__approved-status-deny"
														onChange={this.handleChange(
															"approvedStatus",
														)}
														checked={approvedStatus === "denied"}
													/>
													{lang.t("Denied")}
												</label>
											</div>
										</div>
									</div>
								</If>
							</form>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						id="cancel-location-modal"
						data-test-id="location-modal__cancel"
						className="btn btn-link"
						onClick={
							Object.keys(validLocation).length > 0
								? () => this.setState({ validLocation: {} })
								: () => this.onCancel()
						}
					>
						{lang.t("Cancel")}
					</button>
					<button
						type="button"
						className="btn btn-primary"
						data-test-id="location-modal__save"
						onClick={
							Object.keys(validLocation).length > 0
								? this.confirmLocationCreation
								: this.validateLocation
						}
					>
						<Choose>
							<When condition={savingLocation}>
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</When>
							<Otherwise>
								{Object.keys(validLocation).length > 0
									? lang.t("Confirm")
									: lang.t("Save")}
							</Otherwise>
						</Choose>
					</button>
				</Modal.Footer>
				<Loader active={loading} />
			</Modal>
		);
	}
}

LocationModal.propTypes = {
	show: PropTypes.bool,
	fromLocationApprovals: PropTypes.bool,
	locationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	jobRequestProperties: PropTypes.object,
	contacts: PropTypes.array,
	onSuccess: PropTypes.func,
	onHide: PropTypes.func,
	title: PropTypes.string,
	zonesLoading: PropTypes.bool,
	user: PropTypes.object,
};

LocationModal.defaultProps = {
	show: false,
	fromLocationApprovals: false,
	locationId: undefined,
	projectId: undefined,
	jobRequestProperties: {},
	contacts: [],
	onSuccess: () => {},
	onHide: () => {},
	title: "",
	zonesLoading: false,
	user: {},
};

export default LocationModal;
